import React, { useEffect, useState } from 'react';
import { Hexagon, Plus, Pencil, Trash2, Loader2, MapPin } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { api } from '../../lib/api';
import { toast } from 'sonner';

export default function AdminTradeAreas() {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get('/trade-areas')
      .then(r => setRows(r.data))
      .catch(() => toast.error('Error cargando Trade Áreas'));
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setName(''); setOpen(true); };
  const openEdit = (r) => { setEditing(r.id); setName(r.name); setOpen(true); };
  const editingRow = rows?.find(r => r.id === editing);

  const save = async () => {
    if (!name.trim()) { toast.error('El nombre es requerido'); return; }
    setSaving(true);
    try {
      if (editing) { await api.put(`/trade-areas/${editing}`, { name }); toast.success('Trade Área actualizada'); }
      else { await api.post('/trade-areas', { name }); toast.success('Trade Área creada'); }
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al guardar'); }
    finally { setSaving(false); }
  };

  const remove = async (r) => {
    if (!window.confirm(`¿Eliminar la Trade Área "${r.name}"?`)) return;
    try { await api.delete(`/trade-areas/${r.id}`); toast('Trade Área eliminada'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error al eliminar', { duration: 7000 }); }
  };

  if (rows === null) return <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-[#8a8579]"/></div>;

  return (
    <div className="space-y-5" data-testid="admin-trade-areas-page">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-serif text-[30px] leading-tight">Trade Áreas</h1>
          <p className="text-[12.5px] text-[#5a5a5a] mt-1">Crea el grupo con un nombre (como las zonas TM) y asigna la TA al editar cada estación. Al aprobar el tracking de una estación del grupo, el mismo push se envía a las demás y sus trackings abiertos se cierran.</p>
        </div>
        <Button onClick={openNew} className="h-10 rounded-full bg-[#1c1c1c] hover:bg-black text-[#f8f6f1] text-[12.5px]" data-testid="ta-new-btn">
          <Plus size={14} className="mr-1.5"/> Nueva Trade Área
        </Button>
      </header>

      <div className="bg-white border border-[#e8e5de] rounded-xl overflow-hidden">
        <table className="w-full text-[12.5px]">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-[#8a8579] border-b border-[#e8e5de]">
              <th className="py-2.5 px-4 font-normal">Trade Área</th>
              <th className="py-2.5 px-2 font-normal">Estaciones asignadas</th>
              <th className="py-2.5 px-2"/>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#f0ede6]">
            {rows.map(r => (
              <tr key={r.id} className="hover:bg-[#fafaf6] cursor-pointer" onClick={() => openEdit(r)} data-testid={`ta-row-${r.id}`}>
                <td className="py-2.5 px-4 font-medium"><span className="inline-flex items-center gap-1.5"><Hexagon size={12} className="text-[#0f1d6f]"/>{r.name}</span></td>
                <td className="py-2.5 px-2"><span className="inline-flex items-center gap-1 text-[#0f1d6f]"><MapPin size={11}/>{r.station_count}</span></td>
                <td className="py-2.5 px-2 text-right whitespace-nowrap">
                  <button onClick={(e) => { e.stopPropagation(); openEdit(r); }} className="p-1.5 rounded hover:bg-[#f3f0e9]" data-testid={`ta-edit-${r.id}`}><Pencil size={13}/></button>
                  <button onClick={(e) => { e.stopPropagation(); remove(r); }} className="p-1.5 rounded hover:bg-[#fee2e2] text-[#991b1b]" data-testid={`ta-delete-${r.id}`}><Trash2 size={13}/></button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={3} className="py-8 text-center text-[#8a8579]">Sin Trade Áreas · crea la primera.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md bg-[#f8f6f1]" data-testid="ta-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{editing ? 'Editar Trade Área' : 'Nueva Trade Área'}</DialogTitle></DialogHeader>
          <div className="space-y-3.5">
            <div>
              <Label className="text-[12px] uppercase tracking-wider text-[#8a8579]">Nombre</Label>
              <Input value={name} onChange={e => setName(e.target.value)} placeholder="Ej. TA Roosevelt" className="mt-1 bg-white" data-testid="ta-name-input"/>
              <p className="text-[10.5px] text-[#8a8579] mt-1">Luego asigna las estaciones al grupo desde Configuración → Estaciones.</p>
            </div>
            {editing && (
              <div data-testid="ta-stations-list">
                <Label className="text-[12px] uppercase tracking-wider text-[#8a8579]">Estaciones asignadas ({editingRow?.stations?.length || 0})</Label>
                {(editingRow?.stations?.length || 0) === 0 ? (
                  <p className="text-[11.5px] text-[#8a8579] mt-1.5">Sin estaciones asignadas · asígnalas desde Configuración → Estaciones.</p>
                ) : (
                  <div className="mt-1.5 max-h-[240px] overflow-y-auto rounded-lg border border-[#e8e5de] bg-white divide-y divide-[#f0ede6]">
                    {editingRow.stations.map(s => (
                      <div key={s.id} className="flex items-center gap-2 px-3 py-2" data-testid={`ta-station-${s.id}`}>
                        <MapPin size={11} className="text-[#8a8579] shrink-0"/>
                        <span className="text-[12px] font-medium flex-1 truncate">{s.name}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${s.is_own ? 'bg-[#e7eeff] text-[#0f1d6f]' : 'bg-[#f3f0e9] text-[#8a8579]'}`}>{s.brand || '—'}</span>
                        {s.bu && <span className="text-[10px] text-[#8a8579] font-mono">{s.bu}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            <Button onClick={save} disabled={saving} className="w-full h-10 rounded-full bg-[#1c1c1c] text-white text-[12.5px]" data-testid="ta-save-btn">
              {saving ? <Loader2 size={13} className="animate-spin mr-1.5"/> : null} {editing ? 'Guardar cambios' : 'Crear Trade Área'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
